import React, { useEffect, useState, useRef, useMemo } from 'react';
import { DateTime } from 'luxon';
import { RRule } from 'rrule';

import { useMediaQuery, Popover, DialogTitle, DialogContent, Button, Grid2, Checkbox, FormControlLabel, FormControl, FormGroup, Select, MenuItem, TextField, IconButton, Typography, InputLabel, FormLabel, RadioGroup, Radio, FormHelperText } from '@mui/material';

import { Form, Field, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useExperiences } from 'core/contexts/experience';
import { useTranslation } from 'core/contexts/localization';

import FutureEventsModal from './FutureEventsModal';
import UnitLinkFormFields from 'modules/Experience/components/UnitLinkFormFields';
import { DatePickerWithInput, TextInput, TimePickerHour, TimePickerMinutes, CurrencyInput, LookUpPriceGroup, Tabs, Checklist } from '@utriper/core-ui';

import { useStyles } from './styles';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { validateUnitCapacity } from '@utriper/core';
import { EntityService } from 'core/api/services/lookUp';

function getOffsetForLoc(loc, date = new Date()) {
    // Use Intl.DateTimeFormat to get offset
    let opts = { hour: 'numeric', timeZone: loc, timeZoneName: 'short' };
    let getOffset = (lang) =>
        new Intl.DateTimeFormat(lang, opts).formatToParts(date).reduce((acc, part) => {
            acc[part.type] = part.value;
            return acc;
        }, {}).timeZoneName;
    let offset = getOffset('en');
    // If offset is an abbreviation, change language
    if (!/^UCT|GMT/.test(offset)) {
        offset = getOffset('fr');
    }
    // Remove GMT/UTC
    return offset.substring(3);
}
function TimeZoneFormattedUtils(tz) {
    return `${tz} (GMT${getOffsetForLoc(tz)})`;
}
const TIME_ZONES = Intl.supportedValuesOf('timeZone').map((tz) => {
    return { id: tz, name: TimeZoneFormattedUtils(tz) };
});

const dayExtractor = (rruleString) => {
    // Parse the RRULE
    const rule = RRule.fromString(rruleString);

    // Function to get the days of the week from the RRULE
    const getDaysOfWeekFromRRule = (rule) => {
        const byDay = rule.options.byweekday;
        const daysOfWeek = {
            0: 'MO',
            1: 'TU',
            2: 'WE',
            3: 'TH',
            4: 'FR',
            5: 'SA',
            6: 'SU',
        };

        return byDay.map((day) => daysOfWeek[Number(day).toString()]);
    };
    // Get the days of the week
    return getDaysOfWeekFromRRule(rule);
};

const recurringLimitExtractor = (rruleString) => {
    const rule = RRule.fromString(rruleString);

    const { count = 0, until = false } = rule.options;

    if (count) {
        return { type: 'count', value: count };
    }
    if (until) {
        return { type: 'until', value: until };
    }
    return false;
};

const TAB_FORMS = {
    DETAILS: 0,
    UNITS: 1,
    // REPEAT: 2, // TODO: MOVE REPEAT CONTENT TO NEW TAB
};

export const ExperienceScheduleForm = ({
    range,
    open,
    onDismiss,
    onSave,
    onSelect,
    position: { x, y },
    start,
    end,
    setStart,
    setEnd,
    calendarTz,
    expId, // This should be context?
    subExpId,
    clickedEventValues,
    clickedSchedule,
    getEvents,
    ...props
}) => {
    const { addExperienceSchedule, updateExperienceSchedule, deleteExperienceSchedule } = useExperiences();
    const xs = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const classes = useStyles();
    const { t: translate } = useTranslation();
    const [values, setValues] = useState(clickedEventValues);
    const [tz, setTz] = useState(calendarTz);
    const [anchorEl, setAnchorEl] = useState(null);
    const popoverRef = useRef();
    const [showDeleteFutureEventsModal, setShowDeleteFutureEventsModal] = useState(false);
    const [showSaveFutureEventsModal, setShowSaveFutureEventsModal] = useState(false);
    // array of selected weekdays, following JS getDay() convention, 0 is sunday & 6 is saturday
    const [selectedWeekDays, setSelectedWeekDays] = useState([]);
    const [selectedRecurrenceEnd, setSelectedRecurrenceEnd] = useState(false);
    const [oldStartUTC, setOldStartUTC] = useState(null);
    const [oldEndUTC, setOldEndUTC] = useState();
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [startHour, setStartHour] = useState('00');
    const [endHour, setEndHour] = useState('00');
    const [startMinutes, setStartMinutes] = useState('00');
    const [endMinutes, setEndMinute] = useState('00');
    const [view, setView] = useState(TAB_FORMS.DETAILS);

    const frecuencyOptions = useMemo(
        () => [
            {
                id: 'MO',
                label: xs ? translate('copyScheduleModal.mon') : translate('copyScheduleModal.monday'),
            },
            {
                id: 'TU',
                label: xs ? translate('copyScheduleModal.tue') : translate('copyScheduleModal.tuesday'),
            },
            {
                id: 'WE',
                label: xs ? translate('copyScheduleModal.wed') : translate('copyScheduleModal.wednesday'),
            },
            {
                id: 'TH',
                label: xs ? translate('copyScheduleModal.thu') : translate('copyScheduleModal.thursday'),
            },
            {
                id: 'FR',
                label: xs ? translate('copyScheduleModal.fri') : translate('copyScheduleModal.friday'),
            },
            {
                id: 'SA',
                label: xs ? translate('copyScheduleModal.sat') : translate('copyScheduleModal.saturday'),
            },
            {
                id: 'SU',
                label: xs ? translate('copyScheduleModal.sun') : translate('copyScheduleModal.sunday'),
            },
        ],
        [xs, translate]
    );

    const tabOptions = [
        {
            id: TAB_FORMS.DETAILS,
            label: translate('scheduleForm.tab_options.details'),
        },
        {
            id: TAB_FORMS.UNITS,
            label: translate('scheduleForm.tab_options.units'),
        },
    ];

    const handleChangeView = (view) => setView(view);

    useEffect(() => {
        setValues({ ...clickedEventValues, count: RRule.fromString(clickedEventValues.rRule).options.count || 0, until: RRule.fromString(clickedEventValues.rRule).options.until?.toDateString() || `${translate('copyScheduleModal.date')}` });

        if (clickedEventValues.rRule) {
            if ((Date.parse(values.startUTC) || Date.parse(start)) && (values.timezone || tz)) {
                const utcDate = Date.parse(values.startUTC) ? DateTime.fromISO(values.startUTC, { zone: 'UTC' }) : DateTime.fromISO(start.toISOString(), { zone: 'UTC' });

                const adjustedDate = utcDate.setZone(values.timezone || tz, { keepLocalTime: false });

                const offsetHours = utcDate.hour - adjustedDate.hour + adjustedDate.offset / 60;

                const adjustedWeekDays = clickedEventValues.rRule
                    ? dayExtractor(clickedEventValues.rRule)?.map((day) => {
                          const dayIndex = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].indexOf(day);

                          let adjustedIndex = dayIndex;

                          if (offsetHours < 0) {
                              adjustedIndex = (dayIndex - 1) % 7 >= 0 ? (dayIndex - 1) % 7 : 6; //si da -1, señalar domingo (6)
                          }
                          if (offsetHours > 0) {
                              adjustedIndex = (dayIndex + 1) % 7 <= 6 ? (dayIndex + 1) % 7 : 0;
                          }
                          return ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'][adjustedIndex];
                      })
                    : [];

                const adjustedRecurringLimit = clickedEventValues.rRule ? recurringLimitExtractor(clickedEventValues.rRule) : false;

                setSelectedWeekDays([...adjustedWeekDays]);
                setSelectedRecurrenceEnd(adjustedRecurringLimit);
            }
        } else {
            setSelectedWeekDays([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clickedEventValues, start]);

    useEffect(() => {
        if (!values.timezone && tz) {
            setValues({ ...values, timezone: tz });
        }
    }, [values, tz]);

    useEffect(() => {
        setTz(calendarTz);
    }, [calendarTz]);

    useEffect(() => {
        if ((Date.parse(values.startUTC) || Date.parse(start)) && (values.timezone || tz)) {
            const utcDate = Date.parse(values.startUTC) ? DateTime.fromISO(values.startUTC) : DateTime.fromISO(start.toISOString());
            const adjustedDate = utcDate.setZone(clickedEventValues.timezone || tz);
            setStartDate(adjustedDate.toFormat('ccc LLL dd yyyy'));
            setStartHour(adjustedDate.toISOTime().substring(0, 2));
            setStartMinutes(['00', '15', '30', '45'].includes(adjustedDate.toISOTime().substring(3, 5)) ? adjustedDate.toISOTime().substring(3, 5) : '00');
            setOldStartUTC(start && Date.parse(start) ? start.toISOString() : null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start]);

    useEffect(() => {
        if ((Date.parse(values.endUTC) || Date.parse(end)) && (values.timezone || tz)) {
            const utcDate = Date.parse(values.endUTC) ? DateTime.fromISO(values.endUTC) : start.getTime() === end.getTime() ? DateTime.fromISO(end.toISOString()).plus({ hours: 1 }) : DateTime.fromISO(end.toISOString());
            const adjustedDate = utcDate.setZone(clickedEventValues.timezone || tz);
            setEndDate(adjustedDate.toFormat('ccc LLL dd yyyy'));
            setEndHour(adjustedDate.toISOTime().substring(0, 2));
            setEndMinute(['00', '15', '30', '45'].includes(adjustedDate.toISOTime().substring(3, 5)) ? adjustedDate.toISOTime().substring(3, 5) : '00');
            setOldEndUTC(end && Date.parse(end) ? end.toISOString() : null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [end]);

    const validatePositiveIntegerInput = (value) => {
        const regex = /^[1-9][0-9]*$/;
        return regex.test(value);
    };

    const validateForm = (formValues) => {
        const { units, capacity } = formValues;

        const errors = {};

        if (Number.isInteger(parseInt(capacity)) && !validateUnitCapacity(parseInt(capacity), units)) {
            errors.unitCapacity = 'La capacidad de los tipos de pasajero debe ser menor o igual a la capacidad máxima del slot';
        }

        // TODO: validate other fields
        if (Date.parse(startDate) > Date.parse(values.until) || (values?.repeat && selectedRecurrenceEnd.type === 'until' && !selectedRecurrenceEnd?.value)) {
            errors.recurrenceEnd = 'Invalid recurrence end date';
        }
        return errors;
    };

    const handleSubmit = async () => {
        if (!tz) {
            return;
        }

        if (Date.parse(startDate) > Date.parse(values.until) || (values?.repeat && selectedRecurrenceEnd.type === 'until' && !selectedRecurrenceEnd?.value)) {
            return;
        }

        let data = loadDateIntoValues();
        if (!data.startUTC) {
            return;
        }
        data.timezone = tz;
        const success = await addExperienceSchedule({
            experienceDetailId: subExpId,
            ...data,
        });
        if (success) {
            onDismiss();
            onSave({ data: success, event: 'onSave' });
        }
    };

    const generateDateRange = () => {
        if (!Date.parse(startDate) || !Date.parse(endDate)) {
            toast.error('Fecha Invalida!', { position: 'top-right', theme: 'colored' });
            return;
        }
        const newStartDate = new Date(startDate);
        const newStartDateYear = newStartDate.getFullYear();
        const newStartDateMonth = String(newStartDate.getMonth() + 1).padStart(2, '0');
        const newStartDateDay = String(newStartDate.getDate()).padStart(2, '0');
        const newEndDate = new Date(endDate);
        const newEndDateYear = newEndDate.getFullYear();
        const newEndDateMonth = String(newEndDate.getMonth() + 1).padStart(2, '0');
        const newEndDateDay = String(newEndDate.getDate()).padStart(2, '0');
        const overrideStartZone = DateTime.fromISO(`${newStartDateYear}-${newStartDateMonth}-${newStartDateDay}T${startHour}:${startMinutes}:00`, { zone: values.timezone });
        const overrideEndZone = DateTime.fromISO(`${newEndDateYear}-${newEndDateMonth}-${newEndDateDay}T${endHour}:${endMinutes}:00`, { zone: values.timezone });
        if (overrideStartZone >= overrideEndZone) {
            toast.error('Por favor ingrese una fecha de finalización mayor a la fecha de inicio.', { position: 'top-right', theme: 'colored' });
            return null;
        }
        let oldStartUTCValue = null;
        let oldEndUTCValue = null;
        const luxonOldStartUTC = DateTime.fromISO(oldStartUTC);
        const luxonOldEndUTC = DateTime.fromISO(oldEndUTC);
        if (overrideStartZone.toMillis() !== luxonOldStartUTC.toMillis()) {
            oldStartUTCValue = oldStartUTC;
        }
        if (overrideEndZone.toMillis() !== luxonOldEndUTC.toMillis()) {
            oldEndUTCValue = oldEndUTC;
        }
        const range = {
            startUTC: overrideStartZone.toUTC().toString(),
            endUTC: overrideEndZone.toUTC().toString(),
            oldStartUTC: oldStartUTCValue,
            oldEndUTC: oldEndUTCValue,
            ...generateRRule(overrideStartZone.toUTC()),
        };
        return range;
    };
    const generateRRule = (utcDate) => {
        if (!selectedWeekDays || !selectedWeekDays.length) {
            return { repeat: false };
        }

        const adjustedDate = utcDate.setZone(values.timezone || tz, {
            keepLocalTime: false,
        });

        const offsetHours = utcDate.hour - adjustedDate.hour + adjustedDate.offset / 60;

        const adjustedWeekDays = selectedWeekDays.map((day) => {
            const dayIndex = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].indexOf(day);
            let adjustedIndex = dayIndex;

            if (offsetHours < 0) {
                adjustedIndex = (dayIndex + 1) % 7;
            }
            if (offsetHours > 0) {
                adjustedIndex = (dayIndex - 1) % 7;
            }

            return ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'][adjustedIndex];
        });

        let rrule;

        if (!selectedRecurrenceEnd) {
            //NO END
            rrule = new RRule({
                freq: RRule.WEEKLY,
                byweekday: adjustedWeekDays.map((day) => RRule[day]),
            });
        }

        if (selectedRecurrenceEnd.type === 'count') {
            rrule = new RRule({
                freq: RRule.WEEKLY,
                byweekday: adjustedWeekDays.map((day) => RRule[day]),
                count: values.count,
            });
        }

        if (selectedRecurrenceEnd.type === 'until') {
            rrule = new RRule({
                freq: RRule.WEEKLY,
                byweekday: adjustedWeekDays.map((day) => RRule[day]),
                until: selectedRecurrenceEnd.value,
            });
        }

        return {
            repeat: true,
            rRule: rrule.toString().substring(6),
        };
    };

    const loadDateIntoValues = () => {
        const data = {
            ...values,
            ...generateDateRange(),
        };

        const { price, priceGroupId, priceType } = data;

        if (priceType === priceTypeOptions.FIXED && !Number.isFinite(price)) {
            toast.error('Debes ingresar un precio valido', {
                position: 'top-right',
                theme: 'colored',
            });
            return;
        }

        if (priceType === priceTypeOptions.PRICE_GROUP && !Number.isInteger(priceGroupId) && priceGroupId <= 0) {
            toast.error('Debes seleccionar un grupo de precios', {
                position: 'top-right',
                theme: 'colored',
            });
            return;
        }

        if (priceType === priceTypeOptions.FIXED) {
            data.priceGroupId = null;
        }

        if (priceType === priceTypeOptions.PRICE_GROUP) {
            data.price = 0;
        }

        return data;
    };

    const onCloseFutureEventsModal = () => {
        if (showDeleteFutureEventsModal) setShowDeleteFutureEventsModal(false);
        else if (showSaveFutureEventsModal) setShowSaveFutureEventsModal(false);
        setAnchorEl(null);
        onDismiss();
    };

    const priceTypeOptions = {
        FIXED: 1,
        PRICE_GROUP: 2,
    };

    const priceTypeValues = Object.values(priceTypeOptions);

    const getPriceTypeLabel = (priceType) => {
        if (priceType === priceTypeOptions.FIXED) {
            return translate('scheduleForm.fixed_label');
        }
        if (priceType === priceTypeOptions.PRICE_GROUP) {
            return translate('scheduleForm.price_group_label');
        }

        return '';
    };

    // const Content = () => {
    //     return (
    //         <React.Fragment>
    //              Content...
    //         </React.Fragment>
    //     );
    // };
    return (
        <>
            {/* {xs ? (
                <SwipeableDrawer
                    anchor="bottom"
                    open={open}
                    onOpen={onDismiss}
                    onClose={onDismiss}
                    swipeAreaWidth={56}
                    disableSwipeToOpen={false}
                    keepMounted
                    slotProps={{
                        paper: {
                            elevation: 1,
                            sx: { borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem', maxHeight: '50vh' },
                        },
                    }}
                >
                    <Puller />
                    <Content />
                </SwipeableDrawer>
            ) : ( */}
            <Popover
                ref={popoverRef}
                onClose={onDismiss}
                open={open}
                anchorReference="anchorPosition"
                anchorPosition={{ top: y, left: x }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: '680px',
                            width: '100%',
                        },
                    },
                }}
                disableRestoreFocus
                keepMounted
            >
                <DialogTitle>
                    <Grid2 container justifyContent="space-between" alignItems="center">
                        <Grid2>
                            <Typography fontSize="18px" fontWeight="bold" sx={{ color: '#131313' }}>
                                {clickedSchedule ? translate('scheduleForm.editTitle') : translate('scheduleForm.newTitle')}
                            </Typography>
                        </Grid2>
                        <Grid2>
                            <IconButton
                                onClick={() => {
                                    onDismiss();
                                }}
                            >
                                <Close sx={{ color: '#131313' }} />
                            </IconButton>
                        </Grid2>
                    </Grid2>
                </DialogTitle>
                <DialogContent className={classes.content}>
                    <Form
                        initialValues={{ ...values }}
                        onSubmit={handleSubmit}
                        validate={validateForm}
                        mutators={{ ...arrayMutators }}
                        render={({ handleSubmit, form, errors }) => (
                            <form onSubmit={handleSubmit} noValidate>
                                <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" sx={{ '& .MuiInputBase-root': { height: '56px', borderRadius: '8px' } }}>
                                    {/* Tabs */}
                                    <Grid2 container size={12}>
                                        <Tabs
                                            value={view}
                                            options={tabOptions}
                                            onChange={handleChangeView}
                                            variant="scrollable"
                                            scrollButtons={false}
                                            sx={{
                                                '& .MuiTabs-indicator': { backgroundColor: '#131313' },
                                                '& .MuiButtonBase-root': { color: '#131313 !important', textTransform: 'none' },
                                            }}
                                        />
                                    </Grid2>

                                    {view === TAB_FORMS.DETAILS ? (
                                        <>
                                            {/* From Date */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                <InputLabel sx={{ color: '#131313', fontSize: '1em', fontWeight: 'bold' }}>{translate('scheduleForm.from')}</InputLabel>
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        <DatePickerWithInput
                                                            value={startDate}
                                                            onChange={(date) => {
                                                                if (!Date.parse(date)) {
                                                                    return;
                                                                }
                                                                setStartDate(date.toDateString());
                                                            }}
                                                        />
                                                    </Grid2>
                                                    <Grid2 container size={{ xs: 6, md: 4 }} gap="12px">
                                                        <Grid2>
                                                            <TimePickerHour id="startHour" labelId="startHourLabel" value={startHour} onFieldChange={(selectedHour) => setStartHour(selectedHour.id)} />
                                                        </Grid2>
                                                        <Grid2>
                                                            <TimePickerMinutes id="startMinutes" value={startMinutes} onFieldChange={(selected) => setStartMinutes(selected.id)} />
                                                        </Grid2>
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>

                                            {/* To Date */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                <InputLabel sx={{ color: '#131313', fontSize: '1em', fontWeight: 'bold' }}>{translate('scheduleForm.to')}</InputLabel>
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        <DatePickerWithInput
                                                            value={endDate}
                                                            onChange={(date) => {
                                                                if (!Date.parse(date)) {
                                                                    return;
                                                                }
                                                                setEndDate(date.toDateString());
                                                            }}
                                                        />
                                                    </Grid2>
                                                    <Grid2 container size={{ xs: 6, md: 4 }} gap="12px">
                                                        <Grid2>
                                                            <TimePickerHour id="endHour" value={endHour} onChange={(selected) => setEndHour(selected.id)} />
                                                        </Grid2>
                                                        <Grid2>
                                                            <TimePickerMinutes id="endMinutes" value={endMinutes} onChange={(selected) => setEndMinute(selected.id)} />
                                                        </Grid2>
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>

                                            {/* Time Zone */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                <InputLabel sx={{ color: '#131313', fontSize: '1em', fontWeight: 'bold' }}>{translate('scheduleForm.timezone')}</InputLabel>
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap="nowrap">
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        <Field
                                                            name="timezone"
                                                            component={({ input }) => (
                                                                <Select
                                                                    style={{
                                                                        width: '100%',
                                                                    }}
                                                                    variant="outlined"
                                                                    labelId="timezone"
                                                                    id="timezone"
                                                                    value={input.value}
                                                                    onChange={(e) => {
                                                                        setTz(e?.target?.value);
                                                                        setValues({
                                                                            ...values,
                                                                            timezone: e?.target?.value,
                                                                        });
                                                                    }}
                                                                >
                                                                    {TIME_ZONES.map((tz) => (
                                                                        <MenuItem
                                                                            style={{
                                                                                color: 'black',
                                                                            }}
                                                                            key={`tz-${tz.id}`}
                                                                            value={tz.id}
                                                                        >
                                                                            {tz.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            )}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>

                                            {/* Capacity */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                <InputLabel sx={{ color: '#131313', fontSize: '1em', fontWeight: 'bold' }}>{translate('scheduleForm.capacityLabel')}</InputLabel>
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap="nowrap">
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        <Field
                                                            required={!values.unlimited}
                                                            ignoreError={values.unlimited}
                                                            validate={(number) => {
                                                                if (values.unlimited) {
                                                                    return undefined;
                                                                }
                                                                if (!number) {
                                                                    return translate('form.required');
                                                                }
                                                                if (!/^\d*$/.test(number)) {
                                                                    return translate('form.invalid.capacity');
                                                                }
                                                            }}
                                                            className={classes.capacity}
                                                            variant="outlined"
                                                            name="capacity"
                                                            value={values.capacity}
                                                            disabled={values.unlimited}
                                                            defaultValue={values.capacity}
                                                        >
                                                            {({ input }) => (
                                                                <TextField
                                                                    {...input}
                                                                    variant="outlined"
                                                                    placeholder={translate('scheduleForm.capacityPlaceholder')}
                                                                    onChange={(e) => {
                                                                        let value = e.target.value < 0 ? '' : e.target.value;

                                                                        input.onChange(value);
                                                                        setValues({
                                                                            ...values,
                                                                            [e.target.name]: value,
                                                                        });
                                                                    }}
                                                                    type="number"
                                                                />
                                                            )}
                                                        </Field>
                                                    </Grid2>
                                                </Grid2>
                                                {errors?.unitCapacity && <FormHelperText error>{errors.unitCapacity}</FormHelperText>}
                                            </Grid2>

                                            {/* Unlimited Checkbox */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap="nowrap">
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={values.unlimited}
                                                                    onChange={(event) => {
                                                                        setValues({
                                                                            ...values,
                                                                            unlimited: event.target.checked,
                                                                        });
                                                                    }}
                                                                    name="checked"
                                                                    sx={{
                                                                        color: '#858585',
                                                                        '&.Mui-checked': {
                                                                            color: '#137DC5',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={translate('scheduleForm.unlimitedCapacity')}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>

                                            {/* Private Slot */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap="nowrap">
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={values.privateSlot}
                                                                    onChange={(event) => {
                                                                        setValues({
                                                                            ...values,
                                                                            privateSlot: event.target.checked,
                                                                        });
                                                                    }}
                                                                    name="checked"
                                                                    sx={{
                                                                        color: '#858585',
                                                                        '&.Mui-checked': {
                                                                            color: '#137DC5',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={translate('scheduleForm.privateSlot')}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>

                                            {/* Price Type */}
                                            <Grid2 container my={1} width="100%">
                                                <FormControl fullWidth>
                                                    <FormLabel id="price-type-radio-buttons-group-label">{translate('scheduleForm.price_type')}</FormLabel>
                                                    <RadioGroup
                                                        row
                                                        aria-labelledby="price-type-radio-buttons-group-label"
                                                        name="price-type-radio-buttons-group"
                                                        value={values.priceType}
                                                        onChange={(event) => {
                                                            const value = Number(event?.target?.value);

                                                            if (!Number.isInteger(value)) return;

                                                            setValues({
                                                                ...values,
                                                                priceType: value,
                                                            });
                                                        }}
                                                    >
                                                        {priceTypeValues.map((priceType) => (
                                                            <FormControlLabel key={priceType} value={priceType} control={<Radio />} label={getPriceTypeLabel(priceType)} />
                                                        ))}
                                                    </RadioGroup>
                                                </FormControl>
                                            </Grid2>

                                            {/* Price */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
                                                <InputLabel sx={{ color: '#131313', fontSize: '1em', fontWeight: 'bold' }}>{values?.priceType === priceTypeOptions.FIXED ? translate('scheduleForm.price') : translate('scheduleForm.price_group_label')}</InputLabel>
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap="nowrap">
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        {values?.priceType === priceTypeOptions.FIXED ? (
                                                            <Field
                                                                required={true}
                                                                validate={(number) => {
                                                                    if (number === null || number === undefined || number === '') {
                                                                        return translate('form.required');
                                                                    }
                                                                    const numericValue = Number(number);
                                                                    if (isNaN(numericValue) || numericValue < 0) {
                                                                        return translate('scheduleForm.invalid.price');
                                                                    }
                                                                    return undefined;
                                                                }}
                                                                className={classes.capacity}
                                                                variant="outlined"
                                                                name="price"
                                                                currency={values.currency}
                                                                value={values.price}
                                                                defaultValue={values.price}
                                                                onCurrencyChange={(event) => {
                                                                    const selectedCurrency = event || 'COP';
                                                                    setValues({
                                                                        ...values,
                                                                        currency: selectedCurrency,
                                                                    });
                                                                }}
                                                                label=""
                                                                render={({ input, meta, ...rest }) => (
                                                                    <CurrencyInput
                                                                        {...rest}
                                                                        value={typeof input.value === 'number' ? input.value : 0}
                                                                        onChange={(e) => {
                                                                            let value = e < 0 ? '' : e;
                                                                            input.onChange(value);
                                                                            setValues({
                                                                                ...values,
                                                                                price: value,
                                                                            });
                                                                        }}
                                                                    />
                                                                )}
                                                            ></Field>
                                                        ) : (
                                                            <Field
                                                                id="priceGroupId"
                                                                name="priceGroupId"
                                                                render={({ input }) => (
                                                                    <LookUpPriceGroup
                                                                        {...input}
                                                                        label={translate('scheduleForm.lookup_price_group_label')}
                                                                        entityService={{ getList: ({ entity, search, input }) => EntityService.getListByExperienceId({ entity, search, input, experienceId: expId }) }}
                                                                        value={values?.priceGroupId}
                                                                        initialValue={values?.priceGroupId}
                                                                        onChange={(newValue) => {
                                                                            input.onChange(newValue);
                                                                            setValues({
                                                                                ...values,
                                                                                priceGroupId: newValue,
                                                                            });
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        )}
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>

                                            {/* Repeat */}
                                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap="nowrap">
                                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={values.repeat}
                                                                    onChange={(event) => {
                                                                        setValues({
                                                                            ...values,
                                                                            repeat: event.target.checked,
                                                                        });
                                                                    }}
                                                                    name="checked"
                                                                    sx={{
                                                                        color: '#858585',
                                                                        '&.Mui-checked': {
                                                                            color: '#137DC5',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={translate('scheduleForm.repeat')}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>

                                            {/* Frecuency */}
                                            {values?.repeat && (
                                                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                    <InputLabel sx={{ color: '#131313', fontSize: '1em', fontWeight: 'bold' }}>{translate('scheduleForm.frecuencyLabel')}</InputLabel>
                                                    <Field
                                                        name="selectedWeekDays"
                                                        component={Checklist}
                                                        orientation="horizontal"
                                                        options={frecuencyOptions}
                                                        input={{
                                                            value: selectedWeekDays,
                                                            onChange: (newValues) => setSelectedWeekDays(newValues),
                                                        }}
                                                    />
                                                </Grid2>
                                            )}

                                            {/* End Frecuency */}
                                            {values?.repeat && (
                                                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                                    <InputLabel sx={{ color: '#131313', fontSize: '1em', fontWeight: 'bold' }}>{translate('scheduleForm.ends')}</InputLabel>
                                                    <Grid2 container size={12} width="100%" justifyContent="flex-start" alignItems="center" gap="12px" flexWrap="nowrap">
                                                        <FormControl>
                                                            <Grid2 container>
                                                                {/* Never */}
                                                                <Grid2 size={12}>
                                                                    <FormGroup>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={!selectedRecurrenceEnd}
                                                                                    onChange={(value) => setSelectedRecurrenceEnd(false)}
                                                                                    sx={{
                                                                                        color: '#858585',
                                                                                        '&.Mui-checked': {
                                                                                            color: '#137DC5',
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label={translate('scheduleForm.never')}
                                                                        />
                                                                    </FormGroup>
                                                                </Grid2>

                                                                {/* Until Date */}
                                                                <Grid2 container size={12} alignItems="center">
                                                                    <Grid2 size={{ xs: 4, sm: 2 }}>
                                                                        <FormGroup>
                                                                            <FormControlLabel
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={selectedRecurrenceEnd.type === 'until'}
                                                                                        onChange={(value) => {
                                                                                            if (value) {
                                                                                                return setSelectedRecurrenceEnd({ type: 'until', value: '' });
                                                                                            }
                                                                                        }}
                                                                                        sx={{
                                                                                            color: '#858585',
                                                                                            '&.Mui-checked': {
                                                                                                color: '#137DC5',
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                }
                                                                                label={translate('scheduleForm.on')}
                                                                            />
                                                                        </FormGroup>
                                                                    </Grid2>

                                                                    <Grid2 size={{ xs: 8, md: 6 }}>
                                                                        <DatePickerWithInput
                                                                            disabled={!(selectedRecurrenceEnd.type === 'until')}
                                                                            value={values.until}
                                                                            disablePast={true}
                                                                            onChange={(date) => {
                                                                                if (!Date.parse(date) || Date.now() > date) {
                                                                                    return;
                                                                                }
                                                                                //show date on a format, but also save it on utc for back
                                                                                const year = date.getUTCFullYear();
                                                                                const month = String(date.getUTCMonth()).padStart(2, '0');
                                                                                const day = String(date.getUTCDate()).padStart(2, '0');

                                                                                setSelectedRecurrenceEnd({ type: 'until', value: new Date(Date.UTC(year, month, day, 23, 59)) }); //23:59 to include selected date

                                                                                setValues((prev) => ({ ...prev, until: date.toDateString() }));
                                                                            }}
                                                                        />
                                                                    </Grid2>
                                                                </Grid2>

                                                                {/* After */}
                                                                <Grid2 className={classes.marginRow} size={12} container alignItems="center">
                                                                    <Grid2 size={{ xs: 4, sm: 2 }}>
                                                                        <FormGroup>
                                                                            <FormControlLabel
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={selectedRecurrenceEnd.type === 'count'}
                                                                                        onChange={(value) => {
                                                                                            if (value) {
                                                                                                return setSelectedRecurrenceEnd({ type: 'count', value: value.count });
                                                                                            }
                                                                                        }}
                                                                                        sx={{
                                                                                            color: '#858585',
                                                                                            '&.Mui-checked': {
                                                                                                color: '#137DC5',
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                }
                                                                                label={translate('scheduleForm.after')}
                                                                            />
                                                                        </FormGroup>
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 8, sm: 6 }}>
                                                                        <Field
                                                                            required={!values.unlimited}
                                                                            ignoreError={values.unlimited}
                                                                            validate={(number) => {
                                                                                if (selectedRecurrenceEnd.type !== 'count') {
                                                                                    return false;
                                                                                }
                                                                                if (!number) {
                                                                                    return translate('form.required');
                                                                                }
                                                                                if (!validatePositiveIntegerInput(number)) {
                                                                                    return translate('form.invalid.capacity');
                                                                                }
                                                                            }}
                                                                            className={classes.capacity}
                                                                            variant="outlined"
                                                                            label={translate('scheduleForm.occurrences')}
                                                                            name="count"
                                                                            value={values.count}
                                                                            onTextFieldChange={(event) => {
                                                                                setValues((prev) => ({ ...prev, count: event.target.value }));
                                                                            }}
                                                                            component={TextInput}
                                                                            disabled={!(selectedRecurrenceEnd.type === 'count')}
                                                                            defaultValue={values.count}
                                                                        />
                                                                    </Grid2>
                                                                </Grid2>
                                                            </Grid2>
                                                        </FormControl>
                                                    </Grid2>
                                                </Grid2>
                                            )}
                                        </>
                                    ) : view === TAB_FORMS.UNITS ? (
                                        <UnitLinkFormFields form={form} showCapacity />
                                    ) : (
                                        ''
                                    )}

                                    {/* Buttons */}
                                    {clickedSchedule ? (
                                        <Grid2 container width="100%" flexWrap="nowrap" justifyContent="space-between" alignItems="flex-start" gap="8px">
                                            <Button
                                                sx={{
                                                    width: '100%',
                                                    margin: '0.5rem 0',
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#C51313',
                                                    borderRadius: '8px',
                                                }}
                                                variant="contained"
                                                onClick={() => {
                                                    setShowDeleteFutureEventsModal(true);
                                                    setAnchorEl(popoverRef.current);
                                                }}
                                            >
                                                {translate('form.delete')}
                                            </Button>
                                            <Button
                                                sx={{
                                                    width: '100%',
                                                    margin: '0.5rem 0',
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#137DC5',
                                                    borderRadius: '8px',
                                                }}
                                                variant="contained"
                                                disabled={Object.keys(errors)?.length > 0}
                                                onClick={() => {
                                                    if (!values.capacity && !values.unlimited) {
                                                        return;
                                                    }
                                                    setShowSaveFutureEventsModal(true);
                                                    setAnchorEl(popoverRef.current);
                                                }}
                                            >
                                                {translate('form.save')}
                                            </Button>
                                        </Grid2>
                                    ) : (
                                        <Grid2 container width="100%" flexWrap="nowrap" justifyContent="space-between" alignItems="flex-start" gap="8px">
                                            <Button
                                                sx={{
                                                    width: '100%',
                                                    margin: '0.5rem 0',
                                                    color: '#131313',
                                                    fontWeight: 'bold',
                                                    borderRadius: '8px',
                                                    border: 'solid 1px #13131340',
                                                }}
                                                variant="outlined"
                                                onClick={() => {
                                                    onDismiss();
                                                }}
                                            >
                                                {translate('form.cancel')}
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                sx={{
                                                    width: '100%',
                                                    margin: '0.5rem 0',
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#137DC5',
                                                    borderRadius: '8px',
                                                }}
                                            >
                                                {translate('form.save')}
                                            </Button>
                                        </Grid2>
                                    )}
                                </Grid2>

                                <FormSpy
                                    subscription={{ values: true }}
                                    onChange={({ values }) => {
                                        const { units = [] } = values;

                                        // TODO: Improve the entire form so that this is no longer necessary
                                        if (Array.isArray(units) && units?.length > 0) {
                                            setValues({
                                                ...values,
                                                units,
                                            });
                                        }
                                    }}
                                />
                            </form>
                        )}
                    />
                </DialogContent>
            </Popover>
            {/* )} */}

            <FutureEventsModal
                show={showDeleteFutureEventsModal}
                onClose={onCloseFutureEventsModal}
                x={x}
                y={y}
                anchorEl={anchorEl}
                save={false}
                onDelete={async (all = false, thisAndFollowing = false) => {
                    onCloseFutureEventsModal();
                    let data = loadDateIntoValues();
                    if (!data.startUTC) {
                        return;
                    }
                    if (thisAndFollowing) {
                        // update del original

                        const startDate = new Date(data.startUTC);

                        const year = startDate.getUTCFullYear();
                        const month = startDate.getUTCMonth();
                        const day = startDate.getUTCDate() - 1; //-1 a day before

                        const oldByweeekday = RRule.fromString(clickedEventValues.rRule).options.byweekday;

                        const rRule = new RRule({
                            freq: RRule.WEEKLY,
                            byweekday: oldByweeekday,
                            until: new Date(Date.UTC(year, month, day, 23, 59)),
                        });

                        const result = await updateExperienceSchedule({
                            expId,
                            schId: values.id,
                            all: true,
                            ...clickedEventValues,
                            rRule: rRule.toString().substring(6),
                            isDelete: true,
                        });

                        if (result) {
                            return onSave({ data: { ...data, all: true, repeat: true }, event: 'onDelete' });
                        }
                    } else {
                        const result = await deleteExperienceSchedule({
                            expId,
                            schId: values.id,
                            all,
                            ...data,
                        });
                        if (result) {
                            onSave({ data: { ...data, all }, event: 'onDelete' });
                        }
                    }
                }}
            />
            <FutureEventsModal
                show={showSaveFutureEventsModal}
                onClose={onCloseFutureEventsModal}
                x={x}
                y={y}
                anchorEl={anchorEl}
                save={true}
                onSave={async (all = false, thisAndFollowing = false) => {
                    onCloseFutureEventsModal();
                    let data = loadDateIntoValues();
                    if (!data.startUTC) {
                        return;
                    }

                    if (thisAndFollowing) {
                        //modificar evento para que termine acá -> update rrule

                        const startDate = new Date(data.startUTC);

                        const year = startDate.getUTCFullYear();
                        const month = startDate.getUTCMonth();
                        const day = startDate.getUTCDate() - 1; //-1 a day before

                        const newByweeekday = RRule.fromString(values.rRule).options.byweekday;
                        const oldByweeekday = RRule.fromString(clickedEventValues.rRule).options.byweekday;

                        const rRule = new RRule({
                            freq: RRule.WEEKLY,
                            byweekday: oldByweeekday,
                            until: new Date(Date.UTC(year, month, day, 23, 59)),
                        });

                        const result = await updateExperienceSchedule({
                            expId,
                            schId: values.id,
                            all: true,
                            ...clickedEventValues,
                            rRule: rRule.toString().substring(6),
                        });

                        // now generate the new event this and future

                        let newRrule;
                        if (!selectedRecurrenceEnd) {
                            newRrule = new RRule({
                                freq: RRule.WEEKLY,
                                byweekday: newByweeekday,
                            });
                        }

                        if (selectedRecurrenceEnd.type === 'count') {
                            newRrule = new RRule({
                                freq: RRule.WEEKLY,
                                byweekday: newByweeekday,
                                count: values.count,
                            });
                        }

                        if (selectedRecurrenceEnd.type === 'until') {
                            newRrule = new RRule({
                                freq: RRule.WEEKLY,
                                byweekday: newByweeekday,
                                until: selectedRecurrenceEnd.value,
                            });
                        }

                        await addExperienceSchedule({
                            experienceDetailId: subExpId,
                            ...data,
                            rRule: newRrule.toString().substring(6),
                            experienceScheduleCode: data?.experienceScheduleCode,
                            oldCapacity: result?.capacity,
                            oldStartUTC: result?.startUTC,
                            oldUnlimited: result?.unlimited,
                        });

                        if (result) {
                            return onSave({ data: { ...data, all }, event: 'onUpdate' });
                        }
                    } else {
                        const result = await updateExperienceSchedule({
                            expId,
                            schId: values.id,
                            all,
                            ...data,
                        });
                        if (result) {
                            onSave({ data: { ...data, all }, event: 'onUpdate' });
                        }
                    }
                }}
            />
        </>
    );
};
